import { DomElementBase } from "./DomElementBase";
import { DomElementInteractionEventTypes } from "./DomElementInteractionEventTypes";
import { DomElementInteractionEvent } from "./DomElementInteractionEvent";
import { DomLayerElementInterface } from "../config/DomLayerElementInterface";
import { DomElementFrameInterface } from "../config/DomElementFrameInterface";
import { DomLayer } from "../dom-layer";
import * as isMobile from "ismobilejs";

export class DomElementButton extends DomElementBase {
    constructor(
        domLayer: DomLayer,
        imageDirPath: string,
        options: DomLayerElementInterface,
        defaultOptions: DomLayerElementInterface,
        frames: DomElementFrameInterface[]
    ) {
        super(domLayer, imageDirPath, options, defaultOptions, frames);
    }

    public destroy() {
        if (this.element) {
            this.element.removeEventListener("focus", this.onFocus);
            this.element.removeEventListener("mouseover", this.onMouseOver);
            this.element.removeEventListener("mouseout", this.onMouseOut);
            this.element.removeEventListener("mousedown", this.onMouseDown);
            this.element.removeEventListener("mouseup", this.onMouseUp);
            this.element.removeEventListener("click", this.onClick);
            this.element.removeEventListener("touchstart", this.onTouchStart);
            this.element.removeEventListener("touchend", this.onTouchEnd);
        }

        super.destroy();
    }

    protected init(
        options: DomLayerElementInterface,
        defaultOptions: DomLayerElementInterface,
        frames: DomElementFrameInterface[]
    ) {
        super.init(options, defaultOptions, frames);

        if (this.element) {
            this.element.classList.add("dl-element-button");

            // momentarily disable the button to ensure it doesn't play if the mouse pointer is directly above it when created
            (this.element as HTMLButtonElement).disabled = true;

            if (!isMobile.any) this.element.addEventListener("focus", this.onFocus.bind(this));
            this.element.addEventListener("mouseover", this.onMouseOver.bind(this));
            this.element.addEventListener("mouseout", this.onMouseOut.bind(this));
            this.element.addEventListener("mousedown", this.onMouseDown.bind(this));
            this.element.addEventListener("mouseup", this.onMouseUp.bind(this));
            this.element.addEventListener("click", this.onClick.bind(this));
            this.element.addEventListener("touchstart", this.onTouchStart.bind(this));
            this.element.addEventListener("touchend", this.onTouchEnd.bind(this));
        }
    }

    protected enableElement() {
        if (this.element) {
            (this.element as HTMLButtonElement).disabled = false;
        }
    }

    protected emitElementEvent(
        event: MouseEvent | TouchEvent,
        type: DomElementInteractionEventTypes,
        blur?: boolean | undefined
    ) {
        const elementEvent: DomElementInteractionEvent = {
            type: type,
            event: event,
            element: this
        };

        this.addToElementEvent(elementEvent);

        // remove focus from the element
        if (typeof blur === "undefined") blur = false;
        if (blur && this.element) {
            this.element.blur();
        }

        this.emit(type, elementEvent);
        event.preventDefault();
    }

    protected doInteractionAnimation(over: boolean) {
        if (this.element) {
            if (over) {
                this.element.style.transform = "scale(1.1)";
            } else {
                this.element.style.transform = "scale(1.0)";
            }
        }
    }

    /**
     * Called on desktop only.
     * @param event 
     */
    protected onFocus(event: any) {
        this.emitElementEvent(event, DomElementInteractionEventTypes.PointerAudio);
    }

    protected onMouseOver(event: MouseEvent | TouchEvent) {
        this.doInteractionAnimation(true);
        this.emitElementEvent(event, DomElementInteractionEventTypes.MouseOver);

        // considered the mouse audio event
        this.emitElementEvent(event, DomElementInteractionEventTypes.PointerAudio);
    }

    protected onMouseOut(event: MouseEvent | TouchEvent) {
        this.doInteractionAnimation(false);
        this.emitElementEvent(event, DomElementInteractionEventTypes.MouseOver);
    }

    protected onMouseDown(event: MouseEvent | TouchEvent) {
        this.doInteractionAnimation(false);
        this.emitElementEvent(event, DomElementInteractionEventTypes.MouseDown);
    }

    protected onMouseUp(event: MouseEvent | TouchEvent) {
        this.doInteractionAnimation(true);
        this.emitElementEvent(event, DomElementInteractionEventTypes.MouseUp);
    }

    protected onClick(event: MouseEvent | TouchEvent) {
        this.emitElementEvent(event, DomElementInteractionEventTypes.Click, true);

        // considered the mouse tap event
        this.emitElementEvent(event, DomElementInteractionEventTypes.PointerTap);
    }

    protected onTouchStart(event: MouseEvent | TouchEvent) {
        this.doInteractionAnimation(true);
        this.emitElementEvent(event, DomElementInteractionEventTypes.TouchStart);
    }

    protected onTouchEnd(event: MouseEvent | TouchEvent) {
        this.doInteractionAnimation(false);
        this.emitElementEvent(event, DomElementInteractionEventTypes.TouchEnd, true);

        // considered the touch audio event
        this.emitElementEvent(event, DomElementInteractionEventTypes.PointerAudio);

        // considered the touch tap event
        this.emitElementEvent(event, DomElementInteractionEventTypes.PointerTap);
    }

    /**
     * Allows custom properties to be added to the event.
     * @param elementEvent
     */
    protected addToElementEvent(elementEvent: DomElementInteractionEvent) {}
}
